import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import { VerificationBadge } from '../components/shared/Badges';
import StatCard from '../components/shared/StatCard';
import AIRecommendations from '../components/shared/AIRecommendations';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Package, ShoppingCart, FileText, Truck, DollarSign, TrendingUp,
  Plus, ArrowRight, Clock, CheckCircle, AlertCircle, User,
  Building2, Globe, Calculator, Landmark, MessageSquare
} from 'lucide-react';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [business, setBusiness] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const token = localStorage.getItem('atff_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const config = { headers };

      const [statsRes, activityRes, businessRes, inquiriesRes] = await Promise.allSettled([
        axios.get(`${API}/dashboard/stats`, config),
        axios.get(`${API}/dashboard/recent-activity`, config),
        axios.get(`${API}/businesses/me`, config),
        axios.get(`${API}/inquiries?role=seller`, config),
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (activityRes.status === 'fulfilled') setActivity(activityRes.value.data);
      if (businessRes.status === 'fulfilled') setBusiness(businessRes.value.data);
      if (inquiriesRes.status === 'fulfilled') setInquiries(inquiriesRes.value.data.inquiries || []);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const quickActions = [
    { icon: Plus, labelKey: 'dashboard.quickActions.listProduct', href: '/my-products', color: 'bg-continental' },
    { icon: Calculator, labelKey: 'dashboard.quickActions.calculateTariff', href: '/tariff-calculator', color: 'bg-oceanBlue' },
    { icon: FileText, labelKey: 'dashboard.quickActions.generateDocuments', href: '/documents', color: 'bg-tradeGold' },
    { icon: Truck, labelKey: 'dashboard.quickActions.bookLogistics', href: '/logistics', color: 'bg-logistics' },
  ];

  const profileCompletion = business ? 80 : 30;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="flex">
        {/* Shared Collapsible Sidebar */}
        <DashboardSidebar />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-charcoal font-poppins" data-testid="dashboard-title">
              {t('dashboard.welcomeBack')} {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-gray-600">{t('dashboard.subtitle')}</p>
          </div>

          {/* Profile Completion Alert */}
          {!business && (
            <Card className="mb-6 border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">{t('dashboard.completeProfile.title')}</p>
                      <p className="text-sm text-amber-600">{t('dashboard.completeProfile.subtitle')}</p>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/onboarding')} size="sm" className="btn-primary">
                    {t('dashboard.completeProfile.button')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              title={t('dashboard.stats.productsListed')}
              value={stats?.products_listed || 0}
              subtitle={`${stats?.verified_products || 0} ${t('dashboard.stats.verified')}`}
              icon={Package}
              color="continental"
              trend="up"
              trendValue="+12%"
              href="/my-products"
            />
            <StatCard
              title={t('dashboard.stats.totalOrders')}
              value={stats?.total_orders || 0}
              subtitle={`${stats?.pending_orders || 0} ${t('dashboard.stats.pending')}`}
              icon={ShoppingCart}
              color="blue"
              trend="up"
              trendValue="+8%"
              href="/orders"
            />
            <StatCard
              title={t('dashboard.stats.tradeValue')}
              value={`$${(stats?.total_trade_value || 0).toLocaleString()}`}
              subtitle={t('dashboard.stats.totalRevenue')}
              icon={DollarSign}
              color="gold"
              trend="up"
              trendValue="+23%"
              href="/orders"
            />
            <StatCard
              title={t('dashboard.stats.documents')}
              value={stats?.documents_generated || 0}
              subtitle={t('dashboard.stats.certificatesDeclarations')}
              icon={FileText}
              color="orange"
              href="/documents"
            />
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-charcoal mb-4">{t('dashboard.quickActions.title')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <Link key={action.labelKey} to={action.href}>
                  <Card className="card-interactive h-full">
                    <CardContent className="p-4 flex flex-col items-center text-center">
                      <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mb-3`}>
                        <action.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="font-medium text-charcoal">{t(action.labelKey)}</span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="mb-8">
            <AIRecommendations />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Buyer Inquiries */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  {t('dashboard.inquiries.title')}
                </CardTitle>
                <Badge className="bg-blue-100 text-blue-700">{inquiries.filter(i => i.status === 'pending').length} {t('dashboard.inquiries.new')}</Badge>
              </CardHeader>
              <CardContent>
                {inquiries.length > 0 ? (
                  <div className="space-y-3">
                    {inquiries.slice(0, 4).map((inquiry) => (
                      <div 
                        key={inquiry.inquiry_id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                        onClick={() => navigate(`/inquiries/${inquiry.inquiry_id}`)}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-charcoal text-sm">{inquiry.product_name}</p>
                          <p className="text-xs text-gray-500">
                            {t('dashboard.inquiries.from')} {inquiry.buyer_name} • {inquiry.quantity_needed} {t('dashboard.inquiries.units')}
                          </p>
                        </div>
                        <Badge className={`text-xs ${
                          inquiry.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          inquiry.status === 'responded' ? 'bg-blue-100 text-blue-700' :
                          inquiry.status === 'accepted' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {inquiry.status}
                        </Badge>
                      </div>
                    ))}
                    {inquiries.length > 4 && (
                      <Button variant="link" className="w-full text-continental" onClick={() => navigate('/inquiries')}>
                        {t('dashboard.inquiries.viewAll')} {inquiries.length} {t('dashboard.inquiries.inquiries')} <ArrowRight className="w-4 h-4 ms-1 rtl-flip" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('dashboard.inquiries.noInquiries')}</p>
                    <p className="text-sm text-gray-400">{t('dashboard.inquiries.noInquiriesSubtitle')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">{t('dashboard.orders.title')}</CardTitle>
                <Link to="/dashboard/orders" className="text-sm text-continental hover:underline">
                  {t('dashboard.orders.viewAll')}
                </Link>
              </CardHeader>
              <CardContent>
                {activity?.recent_orders?.length > 0 ? (
                  <div className="space-y-4">
                    {activity.recent_orders.slice(0, 3).map((order) => (
                      <div key={order.order_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-charcoal">{order.product_name}</p>
                          <p className="text-sm text-gray-500">{t('dashboard.orders.qty')} {order.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-continental">
                            {order.currency} {order.total_amount?.toLocaleString()}
                          </p>
                          <Badge variant={order.status === 'delivered' ? 'success' : 'secondary'} className="mt-1">
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('dashboard.orders.noOrders')}</p>
                    <Button onClick={() => navigate('/marketplace')} variant="outline" size="sm" className="mt-3">
                      {t('dashboard.orders.browseMarketplace')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Profile Completion */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('dashboard.profileCompletion.title')}</CardTitle>
                <CardDescription>{t('dashboard.profileCompletion.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">{t('dashboard.profileCompletion.progress')}</span>
                    <span className="text-sm font-medium text-continental">{profileCompletion}%</span>
                  </div>
                  <Progress value={profileCompletion} className="h-2" />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-gray-600">{t('dashboard.profileCompletion.accountCreated')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {business ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">{t('dashboard.profileCompletion.businessProfile')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {business?.verification_status === 'verified' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">{t('dashboard.profileCompletion.verificationComplete')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {stats?.products_listed > 0 ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">{t('dashboard.profileCompletion.firstProductListed')}</span>
                  </div>
                </div>

                {!business && (
                  <Button onClick={() => navigate('/onboarding')} className="w-full mt-6 btn-primary">
                    {t('dashboard.profileCompletion.completeButton')}
                    <ArrowRight className="w-4 h-4 ms-2 rtl-flip" />
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
